import React, { useEffect, useState } from "react";
import { CreditCard, Lock, Check, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

export default function AdminPayments() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    stripe_mode: "test",
    stripe_publishable_key: "",
    stripe_secret_key: "",
    stripe_webhook_secret: "",
    paypal_mode: "sandbox",
    paypal_client_id: "",
    paypal_client_secret: "",
    currency: "USD",
  });

  const load = async () => {
    const { data } = await api.get("/admin/payment-settings");
    setSettings(data);
    setForm((f) => ({
      ...f,
      stripe_mode: data.stripe_mode || "test",
      stripe_publishable_key: data.stripe_publishable_key || "",
      paypal_mode: data.paypal_mode || "sandbox",
      paypal_client_id: data.paypal_client_id || "",
      currency: data.currency || "USD",
    }));
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      // Only send secret fields if the user typed something new (so we don't wipe stored values)
      const payload = { ...form };
      if (!payload.stripe_secret_key) delete payload.stripe_secret_key;
      if (!payload.stripe_webhook_secret) delete payload.stripe_webhook_secret;
      if (!payload.paypal_client_secret) delete payload.paypal_client_secret;
      await api.put("/admin/payment-settings", payload);
      toast.success("Payment settings saved (secrets encrypted at rest)");
      setForm({ ...form, stripe_secret_key: "", stripe_webhook_secret: "", paypal_client_secret: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const s = settings || {};

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <p className="eyebrow mb-2">Direct payments</p>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" data-testid="admin-payments-title">Payment Settings</h1>
      <p className="text-slate-600 max-w-2xl mb-8">
        Connect your own Stripe and PayPal accounts. Money from agency signups goes directly to your bank account — not to Foliosy, not to anyone else.
      </p>

      <div className="bento p-6 mb-8 flex items-start gap-4 bg-amber-50 border-amber-200" data-testid="payments-warning">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Checkout flow is not yet wired up.</p>
          <p>
            This panel stores your API credentials securely (encrypted with Fernet at rest). The actual pricing page + Stripe/PayPal checkout buttons need to be built next — just say the word and I'll wire up the full flow end-to-end so new signups can pay you directly.
          </p>
        </div>
      </div>

      <form onSubmit={save}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* STRIPE */}
          <div className="bento p-8" data-testid="stripe-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#635BFF] flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">Stripe</h2>
                <p className="text-xs text-slate-500 font-mono">{s.stripe_secret_configured ? "✓ configured" : "not configured"}</p>
              </div>
            </div>

            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mode</label>
            <div className="flex gap-2 mb-4">
              {["test", "live"].map((m) => (
                <button key={m} type="button" onClick={() => setForm({ ...form, stripe_mode: m })}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium capitalize ${form.stripe_mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`stripe-mode-${m}`}>
                  {m}{m === "live" && " 🔴"}
                </button>
              ))}
            </div>

            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Publishable key</label>
            <input value={form.stripe_publishable_key} onChange={(e) => setForm({ ...form, stripe_publishable_key: e.target.value })}
              placeholder="pk_test_..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 font-mono text-xs"
              data-testid="stripe-pub-key" />

            <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
              Secret key <Lock className="w-3 h-3 text-slate-400" />
            </label>
            <input type="password" value={form.stripe_secret_key} onChange={(e) => setForm({ ...form, stripe_secret_key: e.target.value })}
              placeholder={s.stripe_secret_configured ? "••••••••••••  (leave blank to keep)" : "sk_test_..."}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 font-mono text-xs"
              data-testid="stripe-secret-key" />

            <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
              Webhook signing secret <Lock className="w-3 h-3 text-slate-400" />
            </label>
            <input type="password" value={form.stripe_webhook_secret} onChange={(e) => setForm({ ...form, stripe_webhook_secret: e.target.value })}
              placeholder={s.stripe_webhook_configured ? "••••••••••••  (leave blank to keep)" : "whsec_..."}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-mono text-xs"
              data-testid="stripe-webhook-secret" />

            <p className="text-xs text-slate-400 mt-3">
              Get your keys at <span className="font-mono">dashboard.stripe.com → Developers → API keys</span>
            </p>
          </div>

          {/* PAYPAL */}
          <div className="bento p-8" data-testid="paypal-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#0070BA] flex items-center justify-center text-white font-bold text-sm">PP</div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">PayPal</h2>
                <p className="text-xs text-slate-500 font-mono">{s.paypal_secret_configured ? "✓ configured" : "not configured"}</p>
              </div>
            </div>

            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mode</label>
            <div className="flex gap-2 mb-4">
              {["sandbox", "live"].map((m) => (
                <button key={m} type="button" onClick={() => setForm({ ...form, paypal_mode: m })}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium capitalize ${form.paypal_mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`paypal-mode-${m}`}>
                  {m}{m === "live" && " 🔴"}
                </button>
              ))}
            </div>

            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Client ID</label>
            <input value={form.paypal_client_id} onChange={(e) => setForm({ ...form, paypal_client_id: e.target.value })}
              placeholder="AY5x..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 font-mono text-xs"
              data-testid="paypal-client-id" />

            <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
              Client secret <Lock className="w-3 h-3 text-slate-400" />
            </label>
            <input type="password" value={form.paypal_client_secret} onChange={(e) => setForm({ ...form, paypal_client_secret: e.target.value })}
              placeholder={s.paypal_secret_configured ? "••••••••••••  (leave blank to keep)" : "EL..."}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-mono text-xs"
              data-testid="paypal-client-secret" />

            <p className="text-xs text-slate-400 mt-3">
              Get your keys at <span className="font-mono">developer.paypal.com → My Apps & Credentials</span>
            </p>
          </div>
        </div>

        <div className="bento p-6 flex items-center justify-between" data-testid="payments-save-bar">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Default currency</label>
            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 font-mono text-sm" data-testid="currency-select">
              {["USD", "EUR", "GBP", "CAD", "AUD", "INR"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button type="submit" className="bg-slate-900 text-white rounded-full px-8 py-3 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2" data-testid="save-payments-button">
            <Check className="w-4 h-4" /> Save credentials
          </button>
        </div>
      </form>

      <div className="mt-8 bento p-6 bg-slate-50">
        <p className="eyebrow mb-3">Security notes</p>
        <ul className="text-sm text-slate-700 space-y-2">
          <li>• All secret keys are encrypted at rest with Fernet (AES-128 + HMAC) before hitting the database.</li>
          <li>• Secret values are NEVER sent back to the browser — leave those fields blank to keep the existing saved value.</li>
          <li>• Use <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">test</code> / <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">sandbox</code> modes during development. Switch to <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">live</code> only when you're ready to accept real money.</li>
        </ul>
      </div>
    </div>
  );
}
